import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Search, Plus, Package, Loader2, AlertCircle } from 'lucide-react';
import { pickingService } from '../../../../../services/pickingService';
import { AdditionalItemDto, AddAdditionalLineResponse } from '../../../../../types/picking';
import { NumericInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface AddLineModalProps {
  isOpen: boolean;
  pListId: number;
  onClose: () => void;
  /** Kalles med backend-svaret så kalleren kan koble linja mot aktiv pall (TW 36393700). */
  onSuccess: (result: AddAdditionalLineResponse) => void;
}

export const AddLineModal: React.FC<AddLineModalProps> = ({ isOpen, pListId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<AdditionalItemDto[]>([]);
  // Vareforslag (36334108): NewLineYesNo-utvalget vises i tom-tilstanden slik at
  // operatøren slipper å søke — inntil 20 vises, resten nås via søket.
  const [suggestions, setSuggestions] = useState<AdditionalItemDto[]>([]);
  const [selectedItem, setSelectedItem] = useState<AdditionalItemDto | null>(null);
  const [quantity, setQuantity] = useState<string>('1');
  const [binCode, setBinCode] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const MAX_SUGGESTIONS = 20;
  // Over denne grensen regnes utvalget som ikke-kuratert (masseflagget) → ingen forslag
  const CURATED_SUGGESTIONS_LIMIT = 50;

  // Reset state when modal opens/closes + last vareforslagene (best effort)
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedItem(null);
      setQuantity('1');
      setBinCode('');
      setError(null);
      setSuccessMessage(null);
      // Focus search input
      setTimeout(() => searchInputRef.current?.focus(), 100);

      let cancelled = false;
      pickingService.searchAdditionalItems('')
        .then(items => {
          if (cancelled) return;
          // Forslag vises kun ved KURATERT utvalg: tenants med masseflagget
          // NewLineYesNo (f.eks. alt fra import) skal beholde dagens tomme
          // tom-tilstand — 20 vilkårlige varer av tusenvis er støy, ikke forslag.
          setSuggestions(items.length <= CURATED_SUGGESTIONS_LIMIT ? items : []);
        })
        .catch(() => { /* forslag er tillegg — søket fungerer uansett */ });
      return () => { cancelled = true; };
    }
    setSuggestions([]);
  }, [isOpen]);

  // Debounced search
  const performSearch = useCallback(async (term: string) => {
    if (term.length < 1) {
      // Tomt søkefelt → tilbake til forslagsvisningen
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const results = await pickingService.searchAdditionalItems(term);
      setSearchResults(results);
      if (results.length === 0) {
        setError(t('warehouse.modules.picking.addLine.noItemsFound'));
      }
    } catch (err) {
      setError(t('warehouse.modules.picking.addLine.searchError'));
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setSelectedItem(null);
    setError(null);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleSelectItem = (item: AdditionalItemDto) => {
    setSelectedItem(item);
    setBinCode(item.stdBinCode || item.stdLocationName || '');
    setQuantity('1');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedItem) return;

    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      setError(t('warehouse.modules.picking.addLine.qtyMustBePositive'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await pickingService.addAdditionalLine(pListId, {
        itemNo: selectedItem.itemNo,
        quantity: qty,
        binCode: binCode || undefined
      });

      if (result.success) {
        // Notify parent to refresh and close modal
        onSuccess(result);
        onClose();
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.message || t('warehouse.modules.picking.addLine.addError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getItemTypeLabel = (itemType?: number) => {
    switch (itemType) {
      case 1: return t('warehouse.modules.picking.addLine.itemTypeBatch');
      case 2: return t('warehouse.modules.picking.addLine.itemTypeSerial');
      case 3: return t('warehouse.modules.picking.addLine.itemTypeSpecial');
      default: return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-0 sm:p-4 sm:items-center">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-green-50">
          <div className="flex items-center gap-2">
            <Plus size={20} className="text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('warehouse.modules.picking.addLine.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Success message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
              <Package size={16} />
              {successMessage}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('warehouse.modules.picking.addLine.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-3 min-h-[44px] border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500"
              autoComplete="off"
            />
            {isSearching && (
              <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Vareforslag (tom-tilstand, 36334108) og søkeresultater — samme listevisning */}
          {!selectedItem && (() => {
            const showingSuggestions = searchTerm.trim().length === 0;
            const listItems = showingSuggestions ? suggestions.slice(0, MAX_SUGGESTIONS) : searchResults;
            if (listItems.length === 0) return null;
            return (
              <div>
                {showingSuggestions && (
                  <div className="text-xs font-medium text-gray-500 mb-1">
                    {t('warehouse.modules.picking.addLine.suggestionsTitle', {}, 'Tillatte tilleggsvarer')}
                  </div>
                )}
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                  {listItems.map((item) => {
                    const typeLabel = getItemTypeLabel(item.itemType);
                    return (
                      <button
                        key={item.itemNo}
                        onClick={() => handleSelectItem(item)}
                        className="w-full text-left p-3 hover:bg-green-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{item.itemNo}</span>
                          {typeLabel && (
                            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                              {typeLabel}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-0.5">{item.description1}</div>
                        <div className="text-xs text-gray-400 mt-0.5 flex gap-3">
                          {item.itemBarCode && <span>{t('warehouse.modules.picking.addLine.eanLabel')}: {item.itemBarCode}</span>}
                          {item.stdLocationName && <span>{t('warehouse.modules.picking.addLine.binLabel')}: {item.stdLocationName}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {showingSuggestions && suggestions.length > MAX_SUGGESTIONS && (
                  <div className="text-xs text-gray-400 mt-1">
                    {t('warehouse.modules.picking.addLine.moreSuggestions',
                      { '0': `${suggestions.length - MAX_SUGGESTIONS}` },
                      `+${suggestions.length - MAX_SUGGESTIONS} flere tillatte varer — bruk søket for å finne dem`)}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Selected Item - Quantity and Location */}
          {selectedItem && (
            <div className="space-y-4">
              {/* Selected item card */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{selectedItem.itemNo}</div>
                    <div className="text-sm text-gray-600">{selectedItem.description1}</div>
                  </div>
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.fields.quantity')}</label>
                <NumericInput
                  mode="decimal"
                  quantity
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="0.001"
                  className="w-full px-4 py-3 min-h-[44px] border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Location / BinCode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('warehouse.general.bin')}</label>
                <input
                  type="text"
                  value={binCode}
                  onChange={(e) => setBinCode(e.target.value)}
                  placeholder={t('warehouse.modules.picking.addLine.binPlaceholder')}
                  className="w-full px-4 py-3 min-h-[44px] border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 min-h-[44px] text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            {t('common.buttons.close')}
          </button>
          {selectedItem && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !quantity || parseFloat(quantity) <= 0}
              className="flex-1 px-4 py-3 min-h-[44px] text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t('warehouse.modules.picking.addLine.adding')}
                </>
              ) : (
                <>
                  <Plus size={18} />
                  {t('common.buttons.add')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
